import { makeAutoObservable } from 'mobx'
import { type AxiosResponse, isAxiosError } from 'axios'
import { uiStore } from 'shared/store/uiStore'
import { toastStore } from 'shared/ui'
import { logger } from 'shared/lib'
import { AssignNumberCommonStore, PhoneApi } from 'entities/Phone'
import { InboxesApi } from 'entities/Inbox'
import { type IParamsCreateInbox } from 'entities/Inbox/api/types'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { type NumbersLayerStore } from 'pages/settings/pages/numbers/store/numbersLayerStore'
import { NumbersPath } from 'pages/settings/pages/numbers/route/numbersPath'
import { AddNumberFromAppRoutes } from 'pages/settings/pages/numbers/pages/AddNumberFromApp/route/type'
import { SelectAppAndNumbersStore } from 'pages/settings/ui/SelectAppAndNumbersCommon/store/SelectAppAndNumbersStore'

import { SettingsPath } from 'pages/settings/route/settingsPath'
import {
  type AddNumberFromAppStepsRoutes,
  type AvailableIntegrationKey,
  titleMap,
} from 'pages/settings/pages/numbers/pages/AddNumberFromApp/lib/constants'

export class AddNumberFromAppStepsStore {
  private _currentStep = 1
  private readonly _selectAppAndNumbersStore: SelectAppAndNumbersStore
  private readonly _assignNumberStore: AssignNumberCommonStore

  private readonly numberValidators: Record<
    AvailableIntegrationKey,
    (id: number) => Promise<AxiosResponse<boolean>>
  > = {
    ['aircall']: (id) => PhoneApi.checkAircallNumber(id),
  }

  constructor(private _numbersLayerStore: NumbersLayerStore, private _entities: IEntitiesStore) {
    this._selectAppAndNumbersStore = new SelectAppAndNumbersStore(this._entities)
    this._assignNumberStore = new AssignNumberCommonStore(
      {
        nextClickHandler: this.assignNumber,
        nextButtonName: 'Assign number',
        allowedTabs: ['new'],
      },
      this._entities
    )

    makeAutoObservable(this)
  }

  validateSelectedNumber = async (): Promise<boolean> => {
    const number = this.selectAppAndNumbersStore.selectedNumber
    const integrationKey = this.selectAppAndNumbersStore.selectedIntegrationKey
    const integrationName = this._selectAppAndNumbersStore.selectedIntegration?.name ?? ''
    const supportLink = this._selectAppAndNumbersStore.selectedIntegrationSupportLink

    if (!number || !integrationKey) return false

    try {
      await this.numberValidators[integrationKey](number.id)
      return true
    } catch (error) {
      if (isAxiosError<{ number: string[] }>(error) && error.response) {
        toastStore.add({
          type: 'error',
          title: error.response.data.number[0] || `${integrationName} number is not valid`,
          action: supportLink
            ? {
                text: `Contact ${integrationName}`,
                link: supportLink,
              }
            : undefined,
        })
      } else {
        toastStore.add({ type: 'error', title: 'Something went wrong' })
      }
      return false
    }
  }

  assignNumber = async () => {
    const { newInboxStore } = this.assignNumberStore
    const { selectedNumber, selectedIntegration, selectedNumberVendorId } =
      this.selectAppAndNumbersStore
    const selectedIntegrationName = selectedIntegration?.name ?? ''
    if (!selectedNumber || !selectedNumberVendorId) {
      toastStore.add({
        type: 'error',
        title: 'Please select a number and an integration to assign it to an inbox',
      })
      return
    }

    const isValid = await this.validateSelectedNumber()
    if (!isValid) return

    try {
      const assignInboxData: IParamsCreateInbox = {
        name: newInboxStore.newInboxNameTrimmed,
        vendor_id: selectedNumber.id,
        number_vendor_id: selectedNumberVendorId,
        owner: {
          id:
            this._entities.usersStore.users.length === 1
              ? this._entities.usersStore.users[0].id
              : Number(newInboxStore.activeInboxOwner),
        },
        number: selectedNumber.formatted_number,
        members: newInboxStore.selectedMembers.map((item) => Number(item.id)),
      }

      await InboxesApi.createInbox(assignInboxData)

      toastStore.add({
        type: 'success',
        title: `${selectedIntegrationName} number assigned to inbox`,
      })
      uiStore.changeRouteViaVue({
        path: `/${SettingsPath.numbers.root}`,
      })
    } catch (error) {
      logger.error(error)
      toastStore.add({
        type: 'error',
        title: 'Something went wrong',
      })
    }
  }

  setCurrentStep = (step: number) => {
    this._currentStep = step
  }

  getStep = (direction: -1 | 1 | 0) => {
    const allSteps = Object.values(this.stepMap)
    const currentPageIndex = this._currentStep - 1

    return allSteps[currentPageIndex + direction]
  }

  getStepByPath = (stepPath: AddNumberFromAppStepsRoutes) => {
    const allPaths = Object.entries(this.stepMap)
    const stepIndex = allPaths.findIndex((path) => {
      return path[1] === stepPath
    })

    return stepIndex !== -1 ? stepIndex + 1 : 1
  }

  getStepUrlMap = (stepPath: AddNumberFromAppStepsRoutes) => {
    return `${this.rootPath}/${stepPath}`
  }

  decreaseStep = () => {
    uiStore.changeRoute({
      path: this.getLastUrl,
    })
  }

  nextClickHandler = () => {
    uiStore.changeRoute({
      path: this.getNextUrl,
    })
  }

  getSettingsStep = (path: AddNumberFromAppStepsRoutes) => {
    return this.getStepByPath(path)
  }

  getProgressInfoTitle = (pathname: string) => {
    return titleMap[pathname] || ''
  }

  get selectAppAndNumbersStore() {
    return this._selectAppAndNumbersStore
  }

  get assignNumberStore() {
    return this._assignNumberStore
  }

  get numbersLayerStore() {
    return this._numbersLayerStore
  }

  get rootPath() {
    return NumbersPath.addNumberFromApp.steps
  }

  get stepMap() {
    return {
      1: AddNumberFromAppRoutes.selectAppAndNumbers,
      2: AddNumberFromAppRoutes.assignNumber,
    }
  }

  get getCurrentStep() {
    return this.getStep(0)
  }

  get getNextStep() {
    const step = this.getStep(1)
    if (!step) return this.getCurrentStep
    return step
  }

  get getLastStep() {
    const step = this.getStep(-1)
    if (!step) return this.getCurrentStep
    return step
  }

  get getCurrentUrl() {
    return `${this.rootPath}/${this.getCurrentStep}`
  }

  get getNextUrl() {
    return `${this.rootPath}/${this.getNextStep}`
  }

  get getLastUrl() {
    return `${this.rootPath}/${this.getLastStep}`
  }

  get allStepsLength() {
    return Object.keys(this.stepMap).length
  }
}
